// ── Reading a run's output against a sample (`EX-EDR02`) ─────────────────────
// Pure helpers for the run panel and the run strip. The server's `passed` is
// the verdict on a sample (it ran the problem's own checker); these only find
// the line to point at once it has said "not passed", so the panel never
// calls a sample wrong that the server called right.

import type { SampleRunCaseResult } from "@/features/quiz/coding-task-api";

/** What one sample's run came to, as the panel and the strip tell it. */
export type RunCaseState =
  /** The output matched the expected output. */
  | "match"
  /** It ran to the end and the output did not match. */
  | "differ"
  /** It did not run to the end: time, memory, a crash or the judge. */
  | "failed"
  /** It ran, and nothing compared it (a problem with its own checker). */
  | "unchecked";

export function runCaseStateOf(result: SampleRunCaseResult): RunCaseState {
  if (result.outcome !== "completed") return "failed";
  if (result.passed === true) return "match";
  if (result.passed === false) return "differ";
  return "unchecked";
}

/** A sample the reader should look at: it did not match or did not finish. */
export function isBadRunCase(result: SampleRunCaseResult): boolean {
  const state = runCaseStateOf(result);
  return state === "differ" || state === "failed";
}

/**
 * An output as lines to show and compare: CRLF read as LF, trailing spaces
 * and trailing blank lines dropped, the way a judge's line checker reads it.
 */
export function outputLines(text: string): string[] {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+$/, ""));
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  return lines;
}

/**
 * The index of the first line where `actual` leaves `expected`, or `null` when
 * the two read the same line for line (a token checker can still have said
 * "not passed", and then no line is marked rather than a wrong one).
 */
export function firstDifferingLine(expected: string, actual: string): number | null {
  const want = outputLines(expected);
  const got = outputLines(actual);
  const length = Math.max(want.length, got.length);
  for (let i = 0; i < length; i += 1) {
    if (want[i] !== got[i]) return i;
  }
  return null;
}

/**
 * The compiler's first line that names an error, else its first non-empty
 * line: what the strip shows until `EX-EDR04` points at the line. GCC opens
 * with "main.cpp: In function 'int main()':", which says where and not what.
 */
export function firstLineOf(text: string | null | undefined): string | null {
  if (!text) return null;
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return lines.find((line) => /\berror\b/i.test(line)) ?? lines[0] ?? null;
}
